import type { AxiosRequestConfig } from 'axios';
import {
  Contact,
  Partner,
  PartnerListSummary,
  Tag,
  ApPortalAccount,
  PartnerInsights,
  DunningState,
} from '../models';
import type { CreateContactSchema } from '../models';
import { ModelResource, type PaginatedResult } from './resource';
import { indexOf } from '@stuut/common/src/utils';
import { v2ParamsSerializer } from '@stuut/common/src/api_client';
import type { CommunicationChannel } from '../models/types';
import { QueryParams } from '../query';

interface EnableDisableParams {
  entityType?: 'partner' | 'contact';
  partner?: Partner;
  contact?: Contact;
  eventSource?: string;
  communicationChannel?: string;
}

interface ReassignParams {
  assignee_id: string | null;
  all_open?: boolean;
  all_assigned?: boolean;
  all_previous_assignee?: boolean;
  all_unassigned?: boolean;
}

export class Partners extends ModelResource<Partner> {
  model = Partner;

  resourcePath = 'v1/partners';

  async assignState(partner: Partner, state: DunningState | string): Promise<Partner> {
    const stateId = typeof state === 'string' ? state : state.id;

    const response = await this.api.post(this.path(partner.id, 'assign_state'), {
      data: { state: stateId },
    });

    partner.updateFromJson(response.data);

    return partner;
  }

  /**
   * List partners with minimal PartnerListSummary objects for table views.
   *
   * This method returns only essential fields for rendering partner tables,
   * reducing payload size by ~90% compared to the full Partner model.
   *
   * Use this for:
   * - Partners table list view
   * - Partner group table view
   *
   * Use list() for:
   * - Partner detail pages
   * - Forms and creation flows
   * - Any context requiring nested data (contacts, tags, subsidiaries, etc.)
   */
  async listSummaries(
    args?: AxiosRequestConfig,
    paginatedResult?: false,
  ): Promise<PartnerListSummary[]>;

  async listSummaries(
    args?: AxiosRequestConfig,
    paginatedResult?: true,
  ): Promise<PaginatedResult<PartnerListSummary>>;

  async listSummaries(
    args?: AxiosRequestConfig,
    paginatedResult?: boolean,
  ): Promise<PartnerListSummary[] | PaginatedResult<PartnerListSummary>> {
    let params = args?.params as Record<string, unknown> | undefined;

    // If QueryParams, serialize it
    if (params && params instanceof QueryParams) {
      params = params.serialize({ forApiRequest: true }) as Record<string, unknown>;
    }

    const response = await this.api.get('v2/partners', {
      ...args,
      params,
      paramsSerializer: v2ParamsSerializer,
    });
    const responseData = response.data as PaginatedResult<PartnerListSummary>;

    // Deserialize to model instances
    const serializedData = responseData.data.map((json) => PartnerListSummary.fromJson(json));

    // Return based on paginatedResult parameter
    if (paginatedResult) {
      return {
        data: serializedData,
        total_records: responseData.total_records,
        current_page: responseData.current_page,
        next_page: responseData.next_page,
      };
    }

    return serializedData;
  }

  async createContact(partner: Partner, data: CreateContactSchema): Promise<Contact> {
    return this.api.post(this.path(partner.id, 'contacts'), { data }).then((response) => {
      const contact = Contact.fromJson(response.data);
      partner.contacts.push(contact);

      return contact;
    });
  }

  async deleteContact(partner: Partner, contact: Contact): Promise<void> {
    const idx = indexOf(partner.contacts, (ctc: Contact) => ctc.id === contact.id);
    return this.api
      .delete(this.path(partner.id, 'contacts', contact.id))
      .then((response) => {
        partner.contacts.splice(idx, 1);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  async setPrimary(
    partner: Partner,
    contact: Contact,
    communicationChannel: CommunicationChannel,
  ): Promise<Contact> {
    const data = {};
    let primaryUrl;

    if (communicationChannel === 'email') {
      data['is_primary_email'] = true;
    } else if (communicationChannel === 'call') {
      data['is_primary_phone'] = true;
    }

    if (contact.isPartnerAccountContact) {
      primaryUrl = this.path(partner.id);
    } else {
      primaryUrl = this.path(partner.id, 'contacts', contact.id);
    }

    const response = await this.api.post(primaryUrl, { data });

    partner.contacts.forEach((ctc) => {
      if (communicationChannel === 'email') {
        ctc.is_primary_email = ctc.id === contact.id;
      }

      if (communicationChannel === 'call') {
        ctc.is_primary_phone = ctc.id === contact.id;
      }
    });

    if (!contact.isPartnerAccountContact) {
      contact.updateFromJson(response.data);

      if (communicationChannel === 'email') {
        partner.is_primary_email = false;
      }

      if (communicationChannel === 'call') {
        partner.is_primary_phone = false;
      }
    } else {
      partner.updateFromJson(response.data);
    }

    return response.data;
  }

  async reassign(
    partnerId: string,
    data: ReassignParams,
    partner?: Partner,
  ): Promise<Partner | Contact> {
    const response = await this.api.post(this.path(partnerId, 'reassign'), {
      data,
    });

    if (partner) partner.updateFromJson(response.data);

    return response.data;
  }

  async enable(
    partner: Partner,
    contact: Contact,
    communication_channel: CommunicationChannel,
    event_source: string = 'contact_page',
  ): Promise<Partner | Contact> {
    let enableUrl;
    const data = {
      communication_channel,
      event_source,
    };

    if (contact.isPartnerAccountContact) {
      enableUrl = this.path(partner.id, 'enable');
    } else {
      enableUrl = this.path(partner.id, 'contacts', contact.id, 'enable');
    }

    const response = await this.api.post(enableUrl, { data });

    this.refreshState(response, partner);

    return response.data;
  }

  async disable(
    partner: Partner,
    contact: Contact,
    communication_channel: CommunicationChannel,
    event_source: string = 'contact_page',
  ): Promise<Partner | Contact> {
    let disableUrl;

    const data = {
      communication_channel,
      event_source,
    };

    if (contact.isPartnerAccountContact) {
      disableUrl = this.path(partner.id, 'disable');
    } else {
      disableUrl = this.path(partner.id, 'contacts', contact.id, 'disable');
    }

    const response = await this.api.post(disableUrl, { data });

    this.refreshState(response, partner);

    return response.data;
  }

  async updateContact(
    partner: Partner,
    contact: Contact,
    data: Partial<CreateContactSchema>,
  ): Promise<Contact> {
    return this.api
      .post(this.path(partner.id, 'contacts', contact.id), { data })
      .then((response) => {
        contact.updateFromJson(response.data);
        return contact;
      });
  }

  async refreshState(response, partner) {
    if (response.data.object === 'partner') {
      partner.updateFromJson(response.data);
    } else {
      // async refresh partner
      this.retrieve(partner.id, partner);
    }

    return partner;
  }

  async applyTag(partner: Partner, tag: Tag): Promise<Partner> {
    return this.api
      .post(this.path(partner.id, 'apply_tag'), { data: { tag: tag.id } })
      .then((response) => {
        partner.updateFromJson(response.data);
        return partner;
      });
  }

  async removeTag(partner: Partner, tag: Tag): Promise<Partner> {
    return this.api
      .post(this.path(partner.id, 'remove_tag'), { data: { tag: tag.id } })
      .then((response) => {
        partner.updateFromJson(response.data);
        return partner;
      });
  }

  async sync(partner: Partner): Promise<Partner> {
    const res = await this.api.post(this.path(partner.id, 'sync'));

    partner.updateFromJson(res.data);

    return partner;
  }

  async downloadReport(partner: Partner) {
    return this.api
      .post(this.path(partner.id, 'ap-portal/report-downloads'))
      .then((response) => response.data);
  }

  async toggleOutreachPause(partner: Partner): Promise<Partner> {
    return this.api
      .post(this.path(partner.id, 'toggle-manual-outreach-pauses'))
      .then((response) => {
        partner.updateFromJson(response.data);
        return partner;
      });
  }

  async getInsights(partner: Partner): Promise<PartnerInsights> {
    return this.api
      .get(this.path(partner.id, 'insights'))
      .then((response) => PartnerInsights.fromJson(response.data));
  }
}
